/** @jsxImportSource @emotion/react */
import { Suspense, useState } from 'react'
import PropTypes from 'prop-types'
import { Grid, Drawer, Card } from 'antd'
import utils from 'utils'
import { MenuOutlined } from '@ant-design/icons'
import styled from '@emotion/styled'
import { css } from '@emotion/react'
import { DARK_MODE, BORDER } from 'constants/ThemeConstant'
import { useSelector } from 'react-redux'
import Loading from 'components/shared-components/Loading'
import { Outlet } from 'react-router-dom'
import isPropValid from '@emotion/is-prop-valid'

const { useBreakpoint } = Grid

const MainContent = styled.div(
  ({ hasPageHeader, gutter, isMobile, hasSideContent }) => css`
    min-height: ${hasPageHeader ? '75vh' : '100%'};
    width: 100%;
    grid-column: ${isMobile ? '1 / -1' : hasSideContent ? '2 / 2' : '1 / 2'};
    grid-row: 1;
    padding: 0 ${gutter ? (isMobile ? 14 : 24) : 0}px;
    position: relative;
    overflow: hidden;
  `
)

const SideContent = styled.div(
  ({ currentTheme }) => css`
    grid-row: 1;
    border-inline-end: 1px solid
      ${currentTheme === 'dark'
        ? DARK_MODE.BORDER_BASE_COLOR
        : BORDER.BASE_COLOR};

    > ul {
      border-inline-end: none !important;
    }
  `
)

const InnerAppLayoutWrapper = styled(Card, {
  shouldForwardProp: (prop) => isPropValid(prop) && prop !== 'sideContentWidth',
})`
  margin: 0 !important;
  height: 100%;

  > .ant-card-body {
    display: grid;
    width: 100%;
    height: 100%;
    grid-template-columns: ${({ sideContentWidth }) =>
        sideContentWidth && `${sideContentWidth}px`} 1fr;
    grid-template-rows: 1fr;
    padding-left: 0;
    padding-right: 0;

    &::before,
    &::after {
      content: none !important;
    }
  }
`

const ContentWrapper = styled('div')`
  height: 100%;

  > div {
    position: initial;
  }

  > ul {
    border-inline-end: none !important;
  }
`

const SideContentMobile = (props) => {
  const { sideContent, visible, onSideContentClose } = props

  return (
    <Drawer
      width='max-content'
      placement='left'
      closable={false}
      onClose={onSideContentClose}
      open={visible}
      style={{ width: '90vw', maxWidth: 256 }}
      styles={{
        body: { paddingLeft: 0, paddingRight: 1 },
      }}
    >
      <ContentWrapper>{sideContent}</ContentWrapper>
    </Drawer>
  )
}

export const InnerAppLayout = (props) => {
  const {
    pageHeader,
    sideContentGutter = true,
    sideContentWidth,
    sideContent,
    children,
  } = props
  const isMobile = !utils.getBreakPoint(useBreakpoint()).includes('lg')
  const [visible, setVisible] = useState(false)
  const currentTheme = useSelector((state) => state.theme.currentTheme)
  const sideWith = sideContent ? sideContentWidth || 256 : undefined

  const close = () => {
    setVisible(false)
  }

  const openSideContentMobile = () => {
    setVisible(true)
  }

  return (
    <InnerAppLayoutWrapper sideContentWidth={sideWith}>
      {isMobile && sideContent ? (
        <SideContentMobile
          visible={visible}
          onSideContentClose={close}
          {...props}
        />
      ) : sideContent ? (
        <SideContent currentTheme={currentTheme}>{sideContent}</SideContent>
      ) : null}

      <MainContent
        hasPageHeader={pageHeader}
        isMobile={isMobile}
        gutter={sideContentGutter}
        hasSideContent={sideWith}
        className='test'
      >
        {isMobile && sideContent ? (
          <div
            className={`font-size-lg mb-3 ${
              !sideContentGutter ? 'pt-3 px-3' : ''
            }`}
          >
            <MenuOutlined onClick={() => openSideContentMobile()} />
          </div>
        ) : null}

        <Suspense fallback={<Loading cover='content' />}>
          {children ? children : <Outlet />}
        </Suspense>
      </MainContent>
    </InnerAppLayoutWrapper>
  )
}

InnerAppLayout.propTypes = {
  sideContent: PropTypes.node,
  pageHeader: PropTypes.bool,
  sideContentWidth: PropTypes.number,
  border: PropTypes.bool,
  sideContentGutter: PropTypes.bool,
}

export default InnerAppLayout
